import { useCallback, useEffect, useState } from 'react'

export interface DefaultedField {
  /** The template's suggestion until the editor types, theirs afterwards. */
  value: string
  /** Take the editor's input, which is what stops the default from applying. */
  set: (value: string) => void
  /** Follow the default again, as if the field had never been touched. */
  reset: () => void
}

/**
 * A text field the template fills in, and stops filling in once the editor has
 * had their say. The memo's two titles, its two sluglines and the heading the
 * preview carries are all this: the template names them, moving the dates or
 * picking a type renames them, and a word typed into one of them ends that for
 * good.
 *
 * `reset()` is what a template change uses to hand a field back to the new
 * template - which is why `edited` is a dependency of the effect below rather
 * than only read by it: the flag going false is what re-applies the default.
 */
export function useDefaultedField(defaultValue: string): DefaultedField {
  // Empty rather than the default, which is what the hand-rolled copies this
  // replaced did: the field is filled in by the effect, one paint later.
  const [value, setValue] = useState('')
  const [edited, setEdited] = useState(false)

  useEffect(() => {
    if (!edited) {
      setValue(defaultValue)
    }
  }, [defaultValue, edited])

  const set = useCallback((next: string) => {
    setEdited(true)
    setValue(next)
  }, [])

  const reset = useCallback(() => {
    setEdited(false)
  }, [])

  return { value, set, reset }
}
